import React, { useState } from 'react';
import PropTypes from 'prop-types';

const equipmentData = [
    {
        programs: ['Beginner', 'Intermediate', 'Advanced', 'Fit For Fifty'],
        location: 'At Home, Outdoors, Gym Classes, Gym Machines',
        equipment: [
            'Dumbbells 2-4kg + Barbell 5-10kg (Beginner)',
            'Dumbbells 3-6kg + Barbell 10-20kg (Intermediate)',
            'Dumbbells 4-8kg + Barbell 15-30kg (Advanced)',
        ],
    },
    {
        programs: ['Move', 'Move 2'],
        location: 'At Home',
        equipment: [
            'No equipment is required as Move is designed for a gentle introduction to exercise. Dumbbells or barbells can be used optionally if you have access to a gym, but it is not mandatory for Move participants',
        ],
    },
    {
        programs: ['Lean And Strong'],
        location: 'At Home, Gym Classes, Gym Machines',
        equipment: [
            'A bench you can lie on, with adjustable settings to reach a 60-degree incline and a fully upright position.',
            'Dumbbells ranging between 2-25kg (depending on your strength!)',
            'A barbell is recommended',
            '(barbell exercises can be modified to use dumbbells, a barbell is the most effective option)',
        ],
    },
    {
        programs: ['Learn To Run'],
        location: 'At Home, Gym Machines',
        equipment: [
            'Running shoes (properly fitted), moisture wicking socks, and comfortable running gear',
        ],
    },
    {
        programs: ['10KM Running & Advanced', 'Half Marathon & Advanced Marathon'],
        location: 'Outdoors',
        equipment: [
            'Running shoes (properly fitted), moisture wicking socks, and comfortable running gear',
        ],
    },
    {
        programs: ['Pregnancy', 'Post Baby'],
        location: 'At Home, Outdoors, Gym Classes, Gym Machines',
        equipment: [
            'There isn\'t any specific equipment you require but you can invest in a fitball and portable step if you like',
        ],
    },
    {
        programs: ['Menopause Method'],
        location: 'At Home, Gym Machines',
        equipment: [
            'Dumbbells (2kg, 4kg or 5kg)',
            'Resistance Bands',
            'Stepper',
            'Barbell 5-10kg',
        ],
    },
    {
        programs: ['Perimenopause Method'],
        location: 'At Home',
        equipment: [
            'Dumbbells 2-4kg + Barbell 5-10kg (Beginner)',
            'Dumbbells 3-6kg + Barbell 10-20kg (Intermediate)',
            'Dumbbells 4-8kg + Barbell 15-30kg (Advanced)',
        ],
    },
    {
        programs: ['Future Proof'],
        location: 'At Home',
        equipment: [
            'Dumbbells 2-4kg + Barbell 5-10kg (Beginner)',
            'Dumbbells 3-6kg + Barbell 10-20kg (Intermediate)',
            'Dumbbells 4-8kg + Barbell 15-30kg (Advanced)',
            'Chair on Standby, Resistance Bands, Therabands & Foam Roller',
        ],
    },
];

// ---- Mobile Card Component ----
const EquipmentCard = ({ programs, location, equipment }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div style={cardStyles.card}>
            <div style={cardStyles.cardHeader} onClick={() => setIsOpen(!isOpen)}>
                <div style={cardStyles.headerLeft}>
                    {programs.map((p, i) => (
                        <p key={i} style={cardStyles.programName}>{p}</p>
                    ))}
                    <div style={cardStyles.location}>📍 {location}</div>
                </div>
                <div style={cardStyles.arrow}>{isOpen ? '▲' : '▼'}</div>
            </div>
            {isOpen && (
                <div style={cardStyles.cardBody}>
                    <hr style={cardStyles.divider} />
                    <p style={cardStyles.equipmentTitle}>Equipment Required</p>
                    <ul style={cardStyles.equipmentList}>
                        {equipment.map((e, i) => (
                            <li key={i} style={cardStyles.equipmentItem}>{e}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

EquipmentCard.propTypes = {
    programs: PropTypes.arrayOf(PropTypes.string).isRequired,
    location: PropTypes.string.isRequired,
    equipment: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// ---- Desktop Table Component ----
const EquipmentTableDesktop = () => (
    <table style={tableStyles.table}>
        <colgroup>
            <col style={{ width: '23%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '55%' }} />
        </colgroup>
        <thead>
        <tr>
            <th style={tableStyles.th}>Program</th>
            <th style={tableStyles.th}>Location</th>
            <th style={tableStyles.th}>Equipment</th>
        </tr>
        </thead>
        <tbody>
        {equipmentData.map((row, index) => (
            <tr
                key={index}
                style={index % 2 === 0 ? tableStyles.rowEven : tableStyles.rowOdd}
            >
                <td style={tableStyles.td}>
                    {row.programs.map((p, i) => (
                        <div key={i} style={tableStyles.programName}>{p}</div>
                    ))}
                </td>
                <td style={tableStyles.td}>
                    {row.location.split(',').map((loc, i, arr) => (
                        <div key={i}>{loc.trim()}{i < arr.length - 1 ? ',' : ''}</div>
                    ))}
                </td>
                <td style={tableStyles.td}>
                    {row.equipment.map((e, i) => (
                        <div key={i} style={tableStyles.equipmentItem}>{e}</div>
                    ))}
                </td>
            </tr>
        ))}
        </tbody>
    </table>
);

// ---- Main Component ----
const EquipmentTable = () => {
    return (
        <div>
            <style>{`
        .equipment-desktop { display: block; }
        .equipment-mobile  { display: none;  }

        @media (max-width: 768px) {
          .equipment-desktop { display: none;  }
          .equipment-mobile  { display: block; }
        }
      `}</style>

            {/* Desktop — table */}
            <div className="equipment-desktop">
                <EquipmentTableDesktop />
            </div>

            {/* Mobile — cards */}
            <div className="equipment-mobile">
                {equipmentData.map((item, index) => (
                    <EquipmentCard key={index} {...item} />
                ))}
            </div>
        </div>
    );
};

export default EquipmentTable;

// ---- Card Styles ----
const cardStyles = {
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '12px',
        overflow: 'hidden',
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px',
        cursor: 'pointer',
        backgroundColor: '#82BDE7',
    },
    headerLeft: { flex: 1 },
    programName: {
        margin: '0 0 4px 0',
        fontWeight: 'bold',
        fontSize: '14px',
        color: '#fff',
    },
    location: {
        fontSize: '12px',
        color: '#f0f0f0',
        marginTop: '4px',
    },
    arrow: {
        fontSize: '12px',
        color: '#fff',
        marginLeft: '8px',
    },
    cardBody: {
        padding: '0 16px 16px 16px',
        backgroundColor: '#EFF6FC',
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #C5DCE8',
        margin: '8px 0',
    },
    equipmentTitle: {
        fontWeight: 'bold',
        fontSize: '13px',
        marginBottom: '8px',
        color: '#2C6E8A',
    },
    equipmentList: { paddingLeft: '20px', margin: 0, listStyle: 'none' },
    equipmentItem: {
        fontSize: '13px',
        color: '#3A3A3A',
        marginBottom: '4px',
        lineHeight: '1.5',
    },
};

// ---- Table Styles ----
const tableStyles = {
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: '0 9px',
        fontFamily: 'sans-serif',
        fontSize: '14px',
        backgroundColor: '#fff',
    },
    th: {
        backgroundColor: '#82BDE7',
        color: '#fff',
        padding: '14px 16px',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '15px',
        borderRight: '9px solid #fff',
    },
    rowEven: { backgroundColor: '#EFF6FC' },
    rowOdd: { backgroundColor: '#BEDDF2' },
    td: {
        padding: '12px 16px',
        verticalAlign: 'top',
        color: '#333',
        borderRight: '9px solid #fff',
    },
    programName: {
        // fontWeight: '600',
        marginBottom: '2px',
        // color: '#2C6E8A',
    },
    equipmentItem: {
        marginBottom: '4px',
        lineHeight: '1.6',
    },
};
